import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { FileText, Search, Calendar, Trash2, AlertTriangle, Download, X, ChevronLeft } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface AsphaltReport {
  report_id: number;
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  foreman_name: string;
  report_date: string;
  work_description: string;
  job_needs: string | null;
  crew_took_lunch: boolean;
  plant_name: string | null;
  notes: string | null;
  status: string;
  pdf_url: string | null;
  created_at: string;
}

export const AdminAsphaltReports: React.FC = () => {
  const auth = useContext(AuthContext);
  const [reports, setReports] = useState<AsphaltReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [selectedReport, setSelectedReport] = useState<AsphaltReport | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Access control
  if (!auth?.user || auth.user.role !== 'admin') {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Only administrators can access this page.</p>
      </div>
    );
  }

  const loadReports = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/asphalt-reports?start_date=${selectedDate}&end_date=${selectedDate}`);
      if (response.data.success) {
        setReports(response.data.data);
      }
    } catch (err) {
      console.error('Failed to load asphalt reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [selectedDate]);

  // Fetch PDF when report is selected
  useEffect(() => {
    if (!selectedReport) {
      setPdfUrl(null);
      setPdfError(null);
      setNumPages(null);
      return;
    }

    const fetchPdf = async () => {
      setLoadingPdf(true);
      setPdfError(null);

      try {
        const response = await api.get(
          `/api/asphalt-reports/generate-pdf/${selectedReport.report_id}`,
          { responseType: 'blob' }
        );

        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err: any) {
        console.error('Failed to load PDF:', err);
        setPdfError(err.message || 'Failed to load PDF');
      } finally {
        setLoadingPdf(false);
      }
    };

    fetchPdf();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [selectedReport]);

  const handleDeleteReport = async () => {
    if (!selectedReport) return;

    setDeleting(true);
    try {
      await api.delete(`/api/asphalt-reports/${selectedReport.report_id}`);
      setShowDeleteConfirm(false);
      setSelectedReport(null);
      setPdfUrl(null);
      loadReports();
    } catch (err) {
      console.error('Failed to delete report:', err);
      alert('Failed to delete report. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6',
      padding: '16px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h1 style={{
          margin: '0 0 16px',
          fontSize: '24px',
          fontWeight: '700',
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FileText size={28} color="#f97316" />
          Asphalt Reports
        </h1>

        {/* Date picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Calendar size={20} color="#6b7280" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: '10px 16px',
              fontSize: '16px',
              border: '2px solid #d1d5db',
              borderRadius: '8px',
              backgroundColor: 'white'
            }}
          />
        </div>
      </div>

      {/* Reports list */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            Loading reports...
          </div>
        ) : reports.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <FileText size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <div>No asphalt reports found for this date</div>
          </div>
        ) : (
          <div>
            {reports.map((report) => (
              <div
                key={report.report_id}
                onClick={() => setSelectedReport(report)}
                style={{
                  padding: '16px',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  backgroundColor: selectedReport?.report_id === report.report_id ? '#f0fdf4' : 'white',
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <div>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827'
                    }}>
                      {report.job_name}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280'
                    }}>
                      #{report.bradanna_job_number}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    textAlign: 'right'
                  }}>
                    <div>{report.foreman_name}</div>
                    <div>{new Date(report.report_date).toLocaleDateString()}</div>
                  </div>
                </div>
                {report.work_description && (
                  <div style={{
                    fontSize: '14px',
                    color: '#374151',
                    lineHeight: '1.4',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {report.work_description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {selectedReport && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          {/* Action buttons */}
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            display: 'flex',
            gap: '12px',
            zIndex: 10000
          }}>
            {/* Download button */}
            {pdfUrl && (
              <button
                onClick={async () => {
                  if (!pdfUrl) return;
                  const fileName = `Asphalt_Report_${selectedReport.job_name.replace(/[^a-z0-9]/gi, '_')}_${new Date(selectedReport.report_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }).replace(/\//g, '-')}.pdf`;
                  const link = document.createElement('a');
                  link.href = pdfUrl;
                  link.download = fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(59, 130, 246, 0.9)',
                  border: '2px solid white',
                  borderRadius: '50%',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Download PDF"
              >
                <Download size={24} />
              </button>
            )}

            {/* Delete button */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: 'rgba(239, 68, 68, 0.9)',
                border: '2px solid white',
                borderRadius: '50%',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Delete Report"
            >
              <Trash2 size={24} />
            </button>

            {/* Close button */}
            <button
              onClick={() => {
                setSelectedReport(null);
                setPdfUrl(null);
              }}
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: 'rgba(107, 114, 128, 0.9)',
                border: '2px solid white',
                borderRadius: '50%',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Close"
            >
              <X size={28} />
            </button>
          </div>

          {/* PDF Content */}
          {pdfError ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
              padding: '40px'
            }}>
              <div style={{ color: '#ef4444', fontSize: 48 }}>⚠️</div>
              <div style={{ color: 'white', fontSize: 18, fontWeight: '500' }}>
                Error Loading PDF
              </div>
              <div style={{ color: '#cbd5e1', fontSize: 14, textAlign: 'center', maxWidth: '400px' }}>
                {pdfError}
              </div>
            </div>
          ) : loadingPdf ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e5e7eb',
                borderTop: '4px solid #fff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              <div style={{ color: 'white', fontSize: 18, fontWeight: '500' }}>
                Loading PDF...
              </div>
            </div>
          ) : pdfUrl ? (
            <TransformWrapper>
              <TransformComponent
                contentStyle={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '20px',
                  paddingTop: '80px'
                }}
              >
                <Document
                  file={pdfUrl}
                  onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                  onLoadError={(error) => setPdfError(error.message)}
                  loading={<div style={{ color: 'white' }}>Loading PDF...</div>}
                >
                  {numPages && Array.from(new Array(numPages), (el, index) => (
                    <div
                      key={`page_${index + 1}`}
                      style={{
                        marginBottom: '20px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)'
                      }}
                    >
                      <Page
                        pageNumber={index + 1}
                        renderTextLayer={true}
                        renderAnnotationLayer={false}
                        width={Math.min(window.innerWidth - 80, 800)}
                      />
                    </div>
                  ))}
                </Document>
              </TransformComponent>
            </TransformWrapper>
          ) : null}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedReport && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#fef2f2',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <AlertTriangle size={32} color="#ef4444" />
            </div>
            <h3 style={{
              margin: '0 0 8px',
              fontSize: '20px',
              fontWeight: '700',
              color: '#111827'
            }}>
              Delete Asphalt Report?
            </h3>
            <p style={{
              margin: '0 0 24px',
              fontSize: '14px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              This will permanently delete the asphalt report for <strong>{selectedReport.job_name}</strong> on {new Date(selectedReport.report_date).toLocaleDateString()}. This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#374151',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteReport}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '14px 20px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: deleting ? 0.6 : 1
                }}
              >
                {deleting ? 'Deleting...' : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AdminAsphaltReports;
